import type { AccessControlProvider } from "@refinedev/core";
import { loadPermissions, clearPermissionsCache, hasPermission } from "@/lib/permissions-cache";

// Re-export для обратной совместимости
export { clearPermissionsCache };

/**
 * Map resource+action to permission code
 *
 * Examples:
 * - resource: "users", action: "list" -> "users.read"
 * - resource: "roles", action: "create" -> "rbac.roles.manage"
 *
 * Канонический каталог прав см. в packages/types/src/rbac.ts (ALL_PERMISSIONS).
 */
function mapToPermissionCode(resource: string, action: string): string {
  // Map resource to permission prefix (canonical, согласно ALL_PERMISSIONS)
  // Синхронизировано с usePermission.ts
  const resourcePermissionMap: Record<string, string> = {
    "admin-users": "admin-users",
    users: "users",
    roles: "rbac.roles",
    permissions: "rbac.permissions",
    subscriptions: "subscriptions",
    "subscription-plans": "subscription-plans",
    payments: "payments",
    orders: "orders",
    products: "products",
    "knowledge-documents": "knowledge",
    broadcasts: "broadcasts",
    "support-tickets": "support",
    "bot-tokens": "api-keys",
    integrations: "integrations",
    // Библиотека голосов — часть перевода эфиров, отдельного права не заводим.
    "voice-profiles": "livestreams",
    "daily-stats": "analytics",
    courses: "courses",
    banners: "content",
    announcements: "content",
    "mini-banners": "content",
    articles: "content",
    teachers: "content",
    "telegram-channels": "telegram-channels",
    "channel-memberships": "channel-memberships",
    triggers: "triggers",
    languages: "localization",
    "translation-keys/ui": "localization",
    "translation-keys/bot": "localization",
    ai: "ai",
    "staff-analytics": "staff-analytics",
    "admin/currencies": "currencies",
    "audit-logs": "audit-logs",
    "order-statuses": "order-statuses",
    "user-invitations": "user-invitations",
    "user-entitlements": "users",
    "club-settings": "settings",
    "legal-pages": "legal-pages",
    "user-bonuses": "user-bonuses",
    violations: "violations",
    "telegram-bots": "telegram-bots",
    "pricing-tiers": "pricing-tiers",
    messaging: "support",
    moderation: "moderation",
    posts: "moderation",
    reports: "reports",
    // Livestreams: list/show/create/edit/delete → livestreams.read/.create/.update/.delete (canonical CRUD).
    // Spec-actions (host/manage/analytics) — отдельный namespace livestream.* через spec-блок ниже.
    livestreams: "livestreams",
    tags: "tags",
    "utm-sources": "utm-sources",
    "admin/countries": "countries",
    leads: "crm.leads",
    "lead-funnels": "crm.leads",
    "lead-webhooks": "crm.leads",
    "message-templates": "email-templates",
    // «Скидки» = autoApply-срез PromoCode, права те же что у промокодов.
    discounts: "promo-codes",
    // Базы данных (тенанты): просмотр — admin.databases.view, настройка — admin.tenant-settings.manage.
    databases: "admin.databases",
  };

  // Map action to permission suffix (canonical action verbs)
  const actionPermissionMap: Record<string, string> = {
    list: "read",
    show: "read",
    create: "write",
    edit: "write",
    delete: "write",
    clone: "write",
    // Spec-actions: имя действия мапится в одноимённый suffix
    refund: "refund",
    cancel: "cancel",
    publish: "publish",
    unpublish: "unpublish",
    archive: "archive",
    restore: "restore",
    export: "export",
    send: "send",
    assign: "assign",
    lock: "lock",
    unlock: "unlock",
    approve: "approve",
    reject: "reject",
    close: "close",
    reopen: "reopen",
    sync: "sync",
    kick: "kick",
    ban: "ban",
    mute: "mute",
    unmute: "unmute",
    warn: "warn",
  };

  const resourcePrefix = resourcePermissionMap[resource] ?? resource;
  const actionSuffix = actionPermissionMap[action] ?? action;

  // Livestreams CRUD идёт через canonical livestreams.read/.create/.update/.delete.
  // Spec-action analytics → livestream.analytics (отдельный operational namespace).
  if (resourcePrefix === "livestreams") {
    if (actionSuffix === "analytics") {
      return "livestream.analytics";
    }
    if (actionSuffix === "read") {
      return "livestreams.read";
    }
    if (actionSuffix === "write") {
      // CRUD-split для чувствительных доменов — определяем точное действие
      if (action === "create") {return "livestreams.create";}
      if (action === "delete") {return "livestreams.delete";}
      return "livestreams.update";
    }
  }

  // Analytics: канонический catalog использует .view вместо .read,
  // .manage вместо .write, плюс .export.
  if (resourcePrefix === "analytics") {
    if (actionSuffix === "read") {
      return "analytics.view";
    }
    if (actionSuffix === "write") {
      return "analytics.manage";
    }
    if (actionSuffix === "export") {
      return "analytics.export";
    }
    return `analytics.${actionSuffix}`;
  }

  // RBAC: write-действия (create/update/delete) собраны под .manage.
  if ((resourcePrefix === "rbac.roles" || resourcePrefix === "rbac.permissions") && actionSuffix === "write") {
    return `${resourcePrefix}.manage`;
  }

  if (resourcePrefix === "crm.leads" && actionSuffix === "write") {
    return "crm.leads.manage";
  }

  // Базы данных (тенанты): просмотр списка — admin.databases.view,
  // изменение функционала/брендинга — admin.tenant-settings.manage.
  if (resourcePrefix === "admin.databases") {
    if (actionSuffix === "read") {
      return "admin.databases.view";
    }
    return "admin.tenant-settings.manage";
  }

  // MCP-доступ: у раздела нет .read/.write — просмотр и создание своих токенов по mcp.use.
  if (resourcePrefix === "mcp") {
    return "mcp.use";
  }

  // email-templates: бэкенд защищает все роуты единым правом email-templates.manage
  // (нет раздельных read/write), поэтому любое действие маппится на .manage
  if (resourcePrefix === "email-templates") {
    return "email-templates.manage";
  }

  return `${resourcePrefix}.${actionSuffix}`;
}

/**
 * AccessControlProvider for RBAC
 * Checks user permissions before allowing access to resources
 */
export const accessControlProvider: AccessControlProvider = {
  can: async ({ resource, action }) => {
    // Always allow access to dashboard
    if (resource === "dashboard" || resource === undefined) {
      return { can: true };
    }

    // Always allow certain actions
    if (action === "field" || action === "export" || action === "import") {
      return { can: true };
    }

    const permissions = await loadPermissions();

    // If no permissions loaded (not logged in or error), deny access
    if (permissions.length === 0) {
      // Check if user is logged in
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        return {
          can: false,
          reason: "Необходима авторизация",
        };
      }

      // User is logged in but has no permissions - allow basic access
      // This is for super admin or initial setup
      return { can: true };
    }

    // Map to permission code
    const requiredPermission = mapToPermissionCode(resource, action);

    // Check if user has the required permission using centralized helper
    if (hasPermission(permissions, requiredPermission)) {
      return { can: true };
    }

    if (
      requiredPermission === "crm.leads.read"
      && hasPermission(permissions, "crm.read")
    ) {
      return { can: true };
    }

    if (
      requiredPermission === "crm.leads.manage"
      && hasPermission(permissions, "crm.manage")
    ) {
      return { can: true };
    }

    return {
      can: false,
      reason: `Недостаточно прав для действия "${action}" над ресурсом "${resource}"`,
    };
  },

  options: {
    buttons: {
      // Hide buttons if user doesn't have permission
      hideIfUnauthorized: true,
    },
  },
};
